import { format, formatDistanceToNow, parseISO, isToday, isTomorrow, isYesterday } from "date-fns";
import { sv } from "date-fns/locale";
import { 
  CalendarMonth as CalendarIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { SmartDateInfo } from "../types/ActivityList.types";


export const formatSmartDate = (dateString: string | null): SmartDateInfo => {
  if (!dateString) {
    return { 
      display: "Ej schemalagd", 
      color: "default", 
      icon: <ScheduleIcon fontSize="small" />
    };
  }

  try {
    const date = parseISO(dateString);
    const now = new Date();
    const currentYear = now.getFullYear();
    const dateYear = date.getFullYear();
    
    if (isToday(date)) {
      const timeStr = format(date, "HH:mm");
      if (date < now) {
        return { 
          display: `Idag ${timeStr}`, 
          color: "error",
          icon: <WarningIcon fontSize="small" />
        };
      }
      return { 
        display: `Idag ${timeStr}`, 
        color: "success",
        icon: <ScheduleIcon fontSize="small" />
      };
    }
    
    if (isTomorrow(date)) {
      return { 
        display: `Imorgon ${format(date, "HH:mm")}`, 
        color: "info",
        icon: <CalendarIcon fontSize="small" />
      };
    }
    
    if (isYesterday(date)) {
      return { 
        display: `Igår ${format(date, "HH:mm")}`, 
        color: "error",
        icon: <WarningIcon fontSize="small" />
      };
    }
    
    if (dateYear !== currentYear) {
      if (date < now) {
        return { 
          display: `${format(date, "dd MMM yyyy HH:mm", { locale: sv })}`, 
          color: "error",
          icon: <WarningIcon fontSize="small" />
        };
      }
      return { 
        display: format(date, "dd MMM yyyy HH:mm", { locale: sv }), 
        color: "info",
        icon: <CalendarIcon fontSize="small" />
      };
    }
    
    if (date < now) {
      return { 
        display: `${format(date, "dd MMM HH:mm", { locale: sv })}`, 
        color: "error",
        icon: <WarningIcon fontSize="small" />
      };
    }
    
    return { 
      display: format(date, "dd MMM HH:mm", { locale: sv }), 
      color: "info",
      icon: <CalendarIcon fontSize="small" />
    };
  } catch {
    return { 
      display: "Ogiltigt datum", 
      color: "default", 
      icon: <WarningIcon fontSize="small" />
    };
  }
};

export const formatSmartDateForWorkItem = (dateString: string | null): SmartDateInfo => {
  if (!dateString) {
    return { 
      display: "Ej schemalagd", 
      color: "default", 
      icon: <ScheduleIcon fontSize="small" />
    };
  }

  try {
    const date = parseISO(dateString);
    const now = new Date();
    const currentYear = now.getFullYear();
    const dateYear = date.getFullYear();
    
    // Midnatt tolkas som "inget klockslag satt" — då visas bara datumet
    const hasClockTime = date.getHours() !== 0 || date.getMinutes() !== 0;

    if (isToday(date)) {
      const timeStr = hasClockTime ? ` ${format(date, "HH:mm")}` : "";
      if (date < now) {
        return { 
          display: `Idag${timeStr}`, 
          color: "error",
          icon: <WarningIcon fontSize="small" />
        };
      }
      return { 
        display: `Idag${timeStr}`, 
        color: "success",
        icon: <ScheduleIcon fontSize="small" />
      };
    }
    
    if (isTomorrow(date)) {
      const timeStr = hasClockTime ? ` ${format(date, "HH:mm")}` : "";
      return { 
        display: `Imorgon${timeStr}`, 
        color: "info",
        icon: <CalendarIcon fontSize="small" />
      };
    }
    
    if (isYesterday(date)) {
      const timeStr = hasClockTime ? ` ${format(date, "HH:mm")}` : "";
      return { 
        display: `Igår${timeStr}`, 
        color: "error",
        icon: <WarningIcon fontSize="small" />
      };
    }
    
    if (dateYear !== currentYear) {
      const formatStr = hasClockTime ? "dd MMM yyyy HH:mm" : "dd MMM yyyy";
      if (date < now) {
        return { 
          display: `${format(date, formatStr, { locale: sv })}`, 
          color: "error",
          icon: <WarningIcon fontSize="small" />
        };
      }
      return { 
        display: format(date, formatStr, { locale: sv }), 
        color: "info",
        icon: <CalendarIcon fontSize="small" />
      };
    }
    
    const formatStr = hasClockTime ? "dd MMM HH:mm" : "dd MMM";
    if (date < now) {
      return { 
        display: `${format(date, formatStr, { locale: sv })}`, 
        color: "error",
        icon: <WarningIcon fontSize="small" />
      };
    }
    
    return { 
      display: format(date, formatStr, { locale: sv }), 
      color: "info",
      icon: <CalendarIcon fontSize="small" />
    };
  } catch {
    return { 
      display: "Ogiltigt datum", 
      color: "default", 
      icon: <WarningIcon fontSize="small" />
    };
  }
};

export const formatTimeAgo = (dateString: string | null): string | null => {
  if (!dateString) return null;
  try {
    return formatDistanceToNow(parseISO(dateString), {
      addSuffix: true,
      locale: sv,
    });
  } catch {
    return "Ogiltigt datum";
  }
};